import type { BookIntentSource } from '@apps/main/new-desktop/core/models';
import { ApiQuery } from '@apps/main/modules/api/shared/apiQuery';
import { logException } from '@apps/main/modules/plugins/logException';

import { cookie } from '@libs/cookie';
import { getDomainMarketOrigin } from '@libs/search-service-smart-search';

import { useShareWidgetStore } from '../shareWidget/shareWidgetStore';
import { useStatsigEventsStore } from '../statsigEvents/statsigEventsStore';
import { useUserStore } from '../userStore';

import { buildAutoSaveTripPayload } from './autoSaveTripOnCheckoutLogic';
import type { AutoSaveTripPayload } from './autoSaveTripOnCheckoutLogic';

export const COOKIE_AUTH_NAME = '83D6F85345D8459aB80D97E8B1BB90CE';
const creatorsApi = new ApiQuery('https://creators-api.sembo.com');
// navigateToCheckout only waits up to FLUSH_TIMEOUT_MS for this call before
// redirecting regardless — this timeout isn't for the UI's benefit, it's so
// the request (kept alive across that redirect by keepalive below) can't
// hang open indefinitely in the background if creators-api never responds.
const SAVE_TIMEOUT_MS = 5000;

export async function autoSaveTripOnCheckout(
  source: BookIntentSource
): Promise<void> {
  try {
    const userStore = useUserStore();
    if (!userStore.isUserLoggedIn) {
      return;
    }

    const token = cookie.get(COOKIE_AUTH_NAME);
    if (!token) {
      logException('autoSaveTripOnCheckout: missing auth cookie', { source });
      return;
    }

    const shareWidgetStore = useShareWidgetStore();
    const statsigEventsStore = useStatsigEventsStore();

    const payload = buildAutoSaveTripPayload({
      recipe: shareWidgetStore.recipe,
      price: shareWidgetStore.price,
      selectedDate: shareWidgetStore.selectedDate,
      publishedTripId: shareWidgetStore.publishedTripId,
    });

    statsigEventsStore.logCoreTripAutoSavedAttempt(source);

    // keepalive lets this survive the hard cross-origin navigation to
    // checkout.aspx that follows shortly after this call — navigateToCheckout
    // only waits up to FLUSH_TIMEOUT_MS before redirecting regardless, so
    // without it the browser would cancel the save whenever that redirect
    // fires before the request has finished.
    await creatorsApi.post<AutoSaveTripPayload, unknown>('trips', payload, {
      headers: {
        authorization: `Bearer ${token}`,
        'X-Sembo-Host': getDomainMarketOrigin(),
      },
      keepalive: true,
      signal: AbortSignal.timeout(SAVE_TIMEOUT_MS),
    });
  } catch (error) {
    logException(error, { source });
  }
}
